import { sasDateToJsDate, jsDateToSASDate, sasDateTimeToJsDate, jsDateToSASDateTime, sasTimeToJsDate, jsDateToSASTime, isLeapYear, } from '@sas-dvr/ltjs-sasicu/util/CalendarUtil';
import { isYearWeek } from './formatUtil';
export function createDateTimeSliderStepReducer(formatType, type) {
    const { toDate, toValue } = formatConversions[formatType];
    const dateStep = DateSteps[type];
    return function step(previousValue, action) {
        let nextValue;
        nextValue = toDate(previousValue);
        dateStep(nextValue, { dir: action.type === 'forward' ? 1 : -1 });
        nextValue = toValue(nextValue);
        return nextValue;
    };
}
const formatConversions = {
    date: { toDate: sasDateToJsDate, toValue: jsDateToSASDate },
    datetime: { toDate: sasDateTimeToJsDate, toValue: jsDateToSASDateTime },
    time: { toDate: sasTimeToJsDate, toValue: jsDateToSASTime },
    month: { toDate: sasDateToJsDate, toValue: jsDateToSASDate },
    quarter: { toDate: sasDateToJsDate, toValue: jsDateToSASDate },
    week: { toDate: sasDateToJsDate, toValue: jsDateToSASDate },
};
const DateSteps = {
    second(value, { dir }) {
        value.setSeconds(value.getSeconds() + dir);
    },
    minute(value, { dir }) {
        value.setMinutes(value.getMinutes() + dir);
    },
    hour(value, { dir }) {
        value.setHours(value.getHours() + dir);
    },
    day(value, { dir }) {
        value.setDate(value.getDate() + dir);
    },
    month(value, { dir }) {
        const nextMonth = value.getMonth() + dir;
        value.setDate(Math.min(value.getDate(), getLastDayOfMonth(nextMonth, value.getFullYear())));
        value.setMonth(nextMonth);
    },
    quarter(value, { dir }) {
        const nextMonth = value.getMonth() + 3 * dir;
        value.setDate(Math.min(value.getDate(), getLastDayOfMonth(nextMonth, value.getFullYear())));
        value.setMonth(nextMonth);
    },
    week(value, { dir }) {
        value.setDate(value.getDate() + 7 * dir);
    },
    year(value, { dir }) {
        value.setFullYear(value.getFullYear() + dir);
    },
    decade(value, { dir }) {
        value.setFullYear(value.getFullYear() + 10 * dir);
    },
};
/**
 * Gets the last day of the month.
 *
 * This is coped from sas.hc.m.slider._calculationUtil.calculateAdjacentDateTimeValue
 *
 * Differences:
 * - this impl adds support for leap years
 * - this impl adds bounds checks for monthIndex
 *
 * @param monthNumber 0 based index for the month of the year (0-11 inclusive).
 */
function getLastDayOfMonth(monthIndex, year) {
    year += Math.floor(monthIndex / 12);
    monthIndex %= 12;
    if (monthIndex === 1) {
        return isLeapYear(year) ? 29 : 28;
    }
    else if (monthIndex === 3 || monthIndex === 5 || monthIndex === 8 || monthIndex === 10) {
        return 30;
    }
    else {
        return 31;
    }
}
/**
 * Ported from sas.hc.m.slider._calculationUtil.calculateDateTimeStepWidths
 *
 * @param format
 * @returns [smallStep, mediumStep, largeStep]
 */
export function getDateTimeSteps(formatName, width) {
    // TODO: I don't think this switch handles all the cases for formats that portable handles
    switch (formatName.toUpperCase()) {
        case 'DATE':
        case 'DATEN':
        case 'DAY':
        case 'DTDATE':
        case 'DDMMYY':
        case 'DOWNAME':
        case 'DTWKDATX':
        case 'HDATE':
        case 'HEBDATE':
        case 'JULDAY':
        case 'JULIAN':
        case 'MMDDYY':
        case 'WEEKDATE':
        case 'WORDDATE':
        case 'YYMMDD':
            return ['day', 'month', 'year'];
        case 'DATETIME':
        case 'TIME':
        case 'MMSS':
            return ['second', 'minute', 'hour'];
        case 'HHMM':
            return ['minute', 'minute', 'hour'];
        case 'HOUR':
            return ['hour', 'hour', 'day'];
        case 'DATEYW':
        case 'DTDATEYW':
        case 'WEEKV':
            if (isYearWeek(formatName, width)) {
                return ['week', 'month', 'year'];
            }
            return ['day', 'month', 'year'];
        case 'DTMONYY':
        case 'MMYY':
        case 'MONYY':
        case 'YYMM':
        case 'MONNAME':
            return ['month', 'month', 'year'];
        case 'DTYYQC':
        case 'QTR':
        case 'YYQC':
            return ['quarter', 'year', 'decade'];
        case 'YEAR':
        case 'DTYEAR':
            return ['year', 'year', 'decade'];
        default:
            // NOTE: SECOND is supposed to be MILLISECOND in HC code.
            //     | I don't think this would work with a SAS DateTime (it's in integer seconds)
            return ['second', 'day', 'year'];
    }
}
export function getDateTimeFormatParts(formatter) {
    // NOTE: most of this is adapted from openui-framework/.../sas.hc.m/src/sas/hc/m/DateTimeCompositeUtil.js
    //     | configurePickerFromSASFormat function
    //
    // FIXME: honestly, the differences between this and getDateTimeSteps just look broken.
    //      | pretty much all this code/logic is copied from other openui-framework and old vaviewer.
    //      | This is still missing something or the previous logic is (and has been) incomplete.
    var _a;
    let dateType;
    let showMilliseconds = false;
    // TODO: Original openui code set these. Need to see what affect they have on the TimePicker.
    //let hoursDuration = false;
    //let minutesDuration = false;
    const formatName = (_a = formatter.getFormatName()) === null || _a === void 0 ? void 0 : _a.toUpperCase();
    // TODO: I'm not sure if all of these formats are portable formats or supported by VAN.
    //     | There was logic in the openui code to not use sas formatters for certain formats, but
    //     | I forget if this is just for formatting in certain contexts or for fully ignoring it.
    switch (formatName) {
        case 'DTDATE':
        case 'DATE':
        case 'DATEN':
        case 'WEEKDATE':
        case 'WORDDATE':
        case 'DTWKDATX':
        case 'NLDATE':
        case 'NLDATEMD':
        case 'NLDATEMDL':
        case 'NLDATEMDM':
        case 'NLDATEMDS':
        case 'XYYMMDD':
        case 'NLDATEL':
        case 'NLDATEM':
        case 'NLDATEW':
        case 'NLDATMDT':
        case 'NLDATMMD':
        case 'NLDATMMDL':
        case 'NLDATMMDM':
        case 'NLDATMMDS':
        case 'NLDATMYW':
        case 'NLDATMWN':
        case 'MMDDYY':
        case 'NLDATES':
        case 'DDMMYY':
        case 'YYMMDD':
        case 'YEAR':
        case 'DTYEAR':
        case 'NLDATEYR':
        case 'NLDATMYR':
        case 'DAY':
        case 'DOWNAME':
        case 'NLDATEWN':
        case 'WEEKDAY':
        case 'DDMMYYB':
        case 'DDMMYYC':
        case 'DDMMYYD':
        case 'DDMMYYN':
        case 'DDMMYYP':
        case 'DDMMYYS':
        case 'MMDDYYB':
        case 'MMDDYYC':
        case 'MMDDYYD':
        case 'MMDDYYN':
        case 'MMDDYYP':
        case 'MMDDYYS':
        case 'WEEKDATX':
        case 'YYMMDDB':
        case 'YYMMDDC':
        case 'YYMMDDD':
        case 'YYMMDDN':
        case 'YYMMDDP':
        case 'YYMMDDS':
        case 'HDATE':
        case 'HEBDATE':
        case 'YYQN':
        case 'YYWEEKU':
        case 'YYWEEKV':
        case 'YYWEEKW':
        case 'JULIAN':
        case 'JULDAY':
            dateType = 'date';
            break;
        case 'TIMEAMPM':
        case 'NLDATMTM':
        case 'NLTIMAP':
        case 'TOD':
        case 'NLDATMTZ':
        case 'NLTIME':
            showMilliseconds = true;
            dateType = 'time';
            break;
        case 'TIME':
            showMilliseconds = true;
            //hoursDuration = true;
            dateType = 'time';
            break;
        case 'MMSS':
            showMilliseconds = true;
            //minutesDuration = true;
            dateType = 'time';
            break;
        case 'HHHH': // NOTE: this case is from vaviewer code
        case 'HOUR':
        case 'HHMM':
            //hoursDuration = true;
            dateType = 'time';
            break;
        case 'DATEAMPM':
        case 'DATETIME':
        case 'NLDATM':
        case 'NLDATMAP':
        case 'NLDATML':
        case 'NLDATMM':
        case 'NLDATMS':
        case 'NLDATMW':
        case 'NLDATMWZ':
        case 'NLDATMZ':
        case 'MDYAMPM':
            showMilliseconds = true;
            dateType = 'datetime';
            break;
        case 'DTYYQC':
        case 'YYQC':
        case 'QTR':
        case 'NLDATEYQ':
        case 'NLDATEYQL':
        case 'NLDATEYQM':
        case 'NLDATEYQS':
        case 'YYQ':
        case 'YYQD':
        case 'YYQP':
        case 'YYQR':
        case 'YYQRC':
        case 'YYQRD':
        case 'YYQRN':
        case 'YYQRP':
        case 'YYQRS':
        case 'YYQS':
        case 'YYQZ':
        case 'QTRR':
        case 'NLDATMYQ':
        case 'NLDATMYQL':
        case 'NLDATMYQM':
        case 'NLDATMYQS':
            dateType = 'quarter';
            break;
        case 'DTMONYY':
        case 'MMYY':
        case 'MONYY':
        case 'YYMM':
        case 'MONNAME':
        case 'MONTH':
        case 'MMYYC':
        case 'MMYYD':
        case 'MMYYN':
        case 'MMYYP':
        case 'MMYYS':
        case 'NLDATEYMS':
        case 'NLDATEMN':
        case 'NLDATEYM':
        case 'NLDATEYML':
        case 'NLDATEYMM':
        case 'NLDATMMN':
        case 'NLDATMYM':
        case 'NLDATMYML':
        case 'NLDATMYMM':
        case 'NLDATMYMS':
        case 'YYMON':
            dateType = 'month';
            break;
        case 'DATEYW':
        case 'DTDATEYW':
        case 'WEEKV':
            dateType = 'date';
            if (isYearWeek(formatName || '', formatter.getWidth())) {
                dateType = 'week';
            }
            break;
        default:
            dateType = getSasDateValueType(formatter);
            break;
    }
    showMilliseconds = showMilliseconds && formatter.getDecimalWidth() > 0;
    if (dateType === 'time' || dateType === 'datetime') {
        return { dateType, showMilliseconds };
    }
    else {
        return { dateType };
    }
}
export function getSasDateValueTypeEncoder(formatType) {
    if (['datetime', 'time'].includes(formatType)) {
        return {
            toJsDate: sasDateTimeToJsDate,
            fromJsDate: jsDateToSASDateTime,
        };
    }
    else {
        return {
            toJsDate: sasDateToJsDate,
            fromJsDate: jsDateToSASDate,
        };
    }
}
export function getSasDateValueType(formatter) {
    if (formatter.isTimeFormat()) {
        return 'time';
    }
    else if (formatter.isDateFormat()) {
        return 'date';
    }
    else if (formatter.isDateTimeFormat()) {
        return 'datetime';
    }
    else {
        return undefined;
    }
}
